import type StoreService from '@ember-data/store';
import type EmployeeVisitMetrics from 'garaje/models/employee-visit-metrics';
import type LocationModel from 'garaje/models/location';
import moment from 'moment-timezone';

const DATE_FORMAT = 'YYYY-MM-DD';

interface EmployeeVisitMetricsParams {
  filter: {
    location: string;
    'start-date': string;
    'end-date': string;
    employee?: string;
    scope?: string;
    status?: string;
  };
}

/**
 * Generates parameters used for querying employee-visit-metrics
 *
 * @param  locationId Location ID
 * @param  start Query start date
 * @param  end Query end date
 * @param  options Optional filters (employee, scope, status)
 * @returns tuple of params
 */
export function getParamsForQueryEmployeeVisitMetrics(
  locationId: string,
  start: Date | string,
  end: Date | string,
  options?: {
    employee?: string;
    scope?: string;
    status?: string;
  },
): ['employee-visit-metrics', EmployeeVisitMetricsParams] {
  const startDate = moment(start).format(DATE_FORMAT);
  const endDate = moment(end).format(DATE_FORMAT);
  const params: EmployeeVisitMetricsParams = {
    filter: {
      location: locationId,
      'start-date': startDate,
      'end-date': endDate,
    },
  };

  if (options?.employee) {
    params.filter.employee = options.employee;
  }

  if (options?.scope) {
    params.filter.scope = options.scope;
  }

  if (options?.status) {
    params.filter.status = options.status;
  }

  return ['employee-visit-metrics', params];
}

/**
 * Fetches employee visit metrics for a location and date range
 *
 * @param store - StoreService
 * @param location - LocationModel
 * @param startDate - Query start date
 * @param endDate - Query end date
 * @param options - Optional filters
 * @returns Promise for EmployeeVisitMetrics
 */
export function fetchEmployeeVisitMetrics(
  store: StoreService,
  location: LocationModel,
  startDate: Date | string,
  endDate: Date | string,
  options?: {
    employee?: string;
    scope?: string;
    status?: string;
  },
): Promise<EmployeeVisitMetrics | undefined> {
  const params = getParamsForQueryEmployeeVisitMetrics(location.id, startDate, endDate, options);

  return store.queryRecord(...params);
}
